import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Mail, Lock, User, Building2, Eye, EyeOff, Check, Info } from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const RegisterPage = () => {
  const { register, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: role selection, 2: details (NO plan selection at signup)
  
  const preselectedRole = location.state?.role;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: preselectedRole || "customer",
    business_name: ""
  });

  useEffect(() => {
    if (preselectedRole === "merchant") {
      setStep(2); // Skip to details
    }
  }, [preselectedRole]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (formData.role === "merchant") {
        // NEW FLOW: Register merchant with NO plan (trial/pending), redirect to upload docs
        const result = await register({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          role: "merchant",
          business_name: formData.business_name,
          selected_plan: "trial" // Default to trial until they select a plan after approval
        });

        toast.success("Registration successful! Please upload your documents to get verified.");
        navigate("/merchant/documents");
      } else {
        // Customer registration
        const result = await register({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          role: "customer"
        });
        
        toast.success("Welcome to VEYRONIX!");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex">
      {/* Left Panel - Branding with Gradient */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-500">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1760611656007-f767a8082758?crop=entropy&cs=srgb&fm=jpg&q=85)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        <div className="relative z-10 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <img src={COMPANY_LOGO} alt="V" className="h-10 w-auto brightness-0 invert" />
            <div className="h-8 w-px bg-white/40" />
            <span className="text-white font-brand text-2xl font-bold">VEYRONIX</span>
          </Link>

          <div className="space-y-6">
            <h1 className="font-heading text-4xl font-bold text-white">
              {formData.role === "merchant" 
                ? "Start Selling with VEYRONIX"
                : "Join VEYRONIX Today"}
            </h1>
            <p className="text-indigo-100 text-lg max-w-md">
              {formData.role === "merchant"
                ? "Get your business online with our powerful merchant tools. No monthly fees — just 5% commission per transaction."
                : "Join thousands of merchants and customers on the most powerful business platform."}
            </p>
          </div>

          <div className="text-indigo-200 text-sm">
            © {new Date().getFullYear()} <span className="font-brand">VEYRONIX</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Form (Light Theme) */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 overflow-y-auto bg-white">
        <div className="w-full max-w-md space-y-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-500 hover:text-indigo-600 transition-colors lg:hidden"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>

          {/* Step indicator for merchants - Now only 2 steps at signup */}
          {formData.role === "merchant" && (
            <div className="flex items-center gap-2 mb-4">
              {[1, 2].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500"
                  }`}>
                    {step > s ? <Check className="h-4 w-4" /> : s}
                  </div>
                  {s < 2 && <div className={`w-12 h-0.5 ${step > s ? "bg-indigo-600" : "bg-gray-200"}`} />}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <h2 className="font-heading text-3xl font-bold text-gray-900">
              {step === 1 ? "Create Account" : "Your Details"}
            </h2>
            <p className="text-gray-500">
              {step === 1 ? (
                <>Already have an account?{" "}
                <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium" data-testid="login-link">
                  Sign in
                </Link></>
              ) : "Fill in your details to continue"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {step === 1 && (
              <>
                <div className="space-y-3">
                  <Label className="text-gray-700">I want to</Label>
                  <RadioGroup
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                    className="grid grid-cols-2 gap-4"
                  >
                    <div className={`flex items-center space-x-3 p-4 rounded-xl border cursor-pointer transition-all ${
                      formData.role === "customer" 
                        ? "border-indigo-500 bg-indigo-50" 
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}>
                      <RadioGroupItem value="customer" id="customer" className="text-indigo-500" />
                      <Label htmlFor="customer" className="text-gray-900 cursor-pointer">Buy Products</Label>
                    </div>
                    <div className={`flex items-center space-x-3 p-4 rounded-xl border cursor-pointer transition-all ${
                      formData.role === "merchant" 
                        ? "border-indigo-500 bg-indigo-50" 
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}>
                      <RadioGroupItem value="merchant" id="merchant" className="text-indigo-500" />
                      <Label htmlFor="merchant" className="text-gray-900 cursor-pointer">Sell Products</Label>
                    </div>
                  </RadioGroup>
                </div>

                {formData.role === "merchant" && (
                  <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl animate-fade-in">
                    <p className="text-sm text-indigo-700">
                      <Info className="h-4 w-4 inline mr-2" />
                      No monthly fees! We only charge a <span className="font-semibold">5% commission</span> on each successful transaction. Start selling for free.
                    </p>
                  </div>
                )}

                <Button
                  type="button"
                  onClick={() => setStep(2)}
                  className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full font-medium"
                  data-testid="continue-btn"
                >
                  Continue
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-indigo-500"
                      required
                      data-testid="name-input"
                    />
                  </div>
                </div>

                {formData.role === "merchant" && (
                  <div className="space-y-2 animate-fade-in">
                    <Label htmlFor="business_name" className="text-gray-700">Business Name</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        id="business_name"
                        type="text"
                        placeholder="Your Business Name"
                        value={formData.business_name}
                        onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                        className="pl-10 h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-indigo-500"
                        required={formData.role === "merchant"}
                        data-testid="business-name-input"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-indigo-500"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-700">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10 h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-indigo-500"
                      required
                      minLength={6}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-12 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full font-medium"
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Creating...
                      </div>
                    ) : "Create Account"}
                  </Button>
                </div>

                {/* New Flow Info for Merchants */}
                {formData.role === "merchant" && (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3 mt-4">
                    <h4 className="font-semibold text-gray-900">What happens next?</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-emerald-600">
                        <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Check className="h-3 w-3" />
                        </div>
                        <span>1. Create Account</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs">2</div>
                        <span>Upload Business Documents</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs">3</div>
                        <span>Admin Reviews & Approves</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs">4</div>
                        <span>Start Selling (5% Commission)</span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </form>

          {step === 1 && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleLogin}
                className="w-full h-12 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full"
                data-testid="google-register-btn"
              >
                <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
            </>
          )}

          {formData.role === "merchant" && step === 1 && (
            <p className="text-center text-sm text-gray-500">
              5% commission on each transaction. No hidden fees.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
